import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import useAuth from '../../../../providers/Auth/Auth.provider';
import SettingsService from '../../../../providers/Settings/Settings.service';
import useNotifications from '../../../../providers/Notifications/Notifications.provider';
import { AppSetting } from '../../../../providers/Settings/AppSettings.model';
import TwField, { TwInputTypes } from '../../../../components/TwFields/TwFields';
import { TwButton, TwButtonType } from '../../../../components/TwButtons/TwButtons';

import {
  useUpdateDisclaimerMutation,
  useCreateDisclaimerMutation,
  useSearchDisclaimersQuery
} from '../../../../redux/api/disclaimer/disclaimer.api';
import { LoadDocumentType } from '../../../../redux/api/load/load.model';
import './DocumentSettings.scss';

interface Disclaimers {
  current: {id: string, text: string};
  original: {id: string, text: string};
}

const DocumentSettings = (): JSX.Element => {
  const { getTenantSetting, updateTenantSetting } = useAuth();
  const { addNotification } = useNotifications();

  const brokerEnabled = getTenantSetting('brokered-loads');

  const watermarkEnabled = getTenantSetting('enable-watermark-feature');

  const [watermarkSetting, setWatermarkSetting] = useState<AppSetting | null>(null);
  const [isWatermarkEnabled, setIsWatermarkEnabled] = useState<boolean>(watermarkEnabled);
  const [disclaimers, setDisclaimers] = useState<{[key: string]: Disclaimers}>({
    bol: {current: {id: '', text: ''}, original: {id: '', text: ''}},
    rateConfirmation: {current: {id: '', text: ''}, original: {id: '', text: ''}},
    tripManifest: {current: {id: '', text: ''}, original: {id: '', text: ''}},
  })

  const {
    data: { userData }
  } = useAuth();

  const division_id = userData?.defaultDivision?.id;

  // Fetching disclaimers
  const { data: searchData, isLoading } = useSearchDisclaimersQuery({
    division_id,
    document_types: [LoadDocumentType.billOfLading, LoadDocumentType.rateConfirmation, LoadDocumentType.tripManifest]
  },
  {
    skip: !division_id
  }
);


  const [updateDisclaimer] = useUpdateDisclaimerMutation();
  const [createDisclaimer] = useCreateDisclaimerMutation();

  useEffect(() => {
    (async (): Promise<void> => {
      const settings = await SettingsService.getAppSettings();
      const watermarkSetting = settings.find(
        (setting) => setting.readableId === 'enable-watermark-feature'
      );
      if (watermarkSetting) {
        setWatermarkSetting(watermarkSetting);
        setIsWatermarkEnabled(watermarkSetting.enabled);
      }
    })();
  }, []);

  useEffect(() => {
    if (searchData) {
      const bol = searchData.find(disclaimer => disclaimer.document_type === LoadDocumentType.billOfLading);
      const rc = searchData.find(disclaimer => disclaimer.document_type === LoadDocumentType.rateConfirmation);
      const tripManifest = searchData.find(disclaimer => disclaimer.document_type === LoadDocumentType.tripManifest);

      setDisclaimers({
        bol: {current: {id: bol?.id || '', text: bol?.text || ''}, original: {id: bol?.id || '', text: bol?.text || ''} },
        rateConfirmation: {current: {id: rc?.id || '', text: rc?.text || ''}, original: {id: rc?.id || '', text: rc?.text || ''}},
        tripManifest: {current: {id: tripManifest?.id || '', text: tripManifest?.text || ''}, original: {id: tripManifest?.id || '', text: tripManifest?.text || ''}},
      });
    }
  }, [searchData]);

  const toggleWatermark = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const checked = e.target.checked;
    if (!watermarkSetting) return;

    try {
      await SettingsService.updateSettings({
        settings: [{ id: watermarkSetting.id, enabled: checked }]
      });
      updateTenantSetting({ [watermarkSetting.readableId]: checked });
      setIsWatermarkEnabled(checked);
      addNotification({
        message: 'Watermark setting updated successfully',
        variant: 'success'
      });
    } catch (err) {
      addNotification({
        message: 'Error while updating watermark setting',
        variant: 'error'
      });
    }
  };

  const disclaimerHasChanges = (type: 'bol' | 'rateConfirmation' | 'tripManifest'): boolean => {
    return disclaimers[type].current.text !== disclaimers[type].original.text;
  };

  const handleSave = async (): Promise<void> => {
    try {
      const updatePromises = [];
      if (disclaimers.bol.original.id !== '' && disclaimerHasChanges('bol')) {
        updatePromises.push(
          updateDisclaimer({
            id: disclaimers.bol.original.id,
            document_type: LoadDocumentType.billOfLading,
            disclaimer_text: disclaimers.bol.current.text,
            division_id
          })
        );
      } else if (disclaimers.bol.current.text !== '' && disclaimerHasChanges('bol')) {
        updatePromises.push(
          createDisclaimer({
            document_type: LoadDocumentType.billOfLading,
            disclaimer_text: disclaimers.bol.current.text,
            division_id
          })
        );
      }

      if (disclaimers.rateConfirmation.original.id !== '' && disclaimerHasChanges('rateConfirmation')) {
        updatePromises.push(
          updateDisclaimer({
            id: disclaimers.rateConfirmation.original.id,
            document_type: LoadDocumentType.rateConfirmation,
            disclaimer_text: disclaimers.rateConfirmation.current.text,
            division_id
          })
        );
      } else if (disclaimers.rateConfirmation.current.text !== '' && disclaimerHasChanges('rateConfirmation')) {
        updatePromises.push(
          createDisclaimer({
            document_type: LoadDocumentType.rateConfirmation,
            disclaimer_text: disclaimers.rateConfirmation.current.text,
            division_id
          })
        );
      }

      if (disclaimers.tripManifest.original.id !== '' && disclaimerHasChanges('tripManifest')) {
        updatePromises.push(
          updateDisclaimer({
            id: disclaimers.tripManifest.original.id,
            document_type: LoadDocumentType.tripManifest,
            disclaimer_text: disclaimers.tripManifest.current.text,
            division_id
          })
        );
      } else if (disclaimers.tripManifest.current.text !== '' && disclaimerHasChanges('tripManifest')) {
        updatePromises.push(
          createDisclaimer({
            document_type: LoadDocumentType.tripManifest,
            disclaimer_text: disclaimers.tripManifest.current.text,
            division_id
          })
        );
      }

      await Promise.all(updatePromises);

      setDisclaimers({
        bol: {current: disclaimers.bol.current, original: disclaimers.bol.original},
        rateConfirmation: {current: disclaimers.rateConfirmation.current, original: disclaimers.rateConfirmation.original},
        tripManifest: {current: disclaimers.tripManifest.current, original: disclaimers.tripManifest.original},
      });

      addNotification({
        message: 'Document settings updated successfully',
        variant: 'success'
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      addNotification({
        message: 'Error while updating document settings',
        variant: 'error'
      });
    }
  };

  const handleDiscard = (): void => {
    setDisclaimers({
      bol: {current: disclaimers.bol.original, original: disclaimers.bol.original},
      rateConfirmation: {current: disclaimers.rateConfirmation.original, original: disclaimers.rateConfirmation.original},
      tripManifest: {current: disclaimers.tripManifest.original, original: disclaimers.tripManifest.original},
    });
  };

  const hasChanges = useMemo(() => {
    return disclaimers.bol.current.text !== disclaimers.bol.original.text ||
      disclaimers.rateConfirmation.current.text !== disclaimers.rateConfirmation.original.text ||
      disclaimers.tripManifest.current.text !== disclaimers.tripManifest.original.text;
  }, [disclaimers]);

  return (
    <Box>
      <Box className="app-settings__container">
        <Box className="left_container">
          <Typography variant="h5" className="app-settings__subtitle">
            Documents
          </Typography>
          <Typography className="settings__subtitle">Manage your document settings</Typography>
        </Box>
        <Box className="right_container">
          <Box className="document-settings__section">
            <Typography variant="h6" className="document-settings__section-title">
              Bill of Lading Disclaimer
            </Typography>
            <TwField
              inputType={TwInputTypes.textarea}
              name="bolDisclaimer"
              value={disclaimers.bol.current.text}
              onInputChange={(e): void => {
                  setDisclaimers({...disclaimers, bol: {...disclaimers.bol, current: {...disclaimers.bol.current, text: e.target.value || ''}}})

              }}
              placeholder="Enter Bill of Lading Disclaimer"
            />
          </Box>

          {brokerEnabled && (
            <Box className="document-settings__section">
              <Typography variant="h6" className="document-settings__section-title">
                Rate Confirmation Disclaimer
              </Typography>
              <TwField
                inputType={TwInputTypes.textarea}
                name="rcDisclaimer"
                value={disclaimers.rateConfirmation.current.text}
                onInputChange={(e): void => setDisclaimers({...disclaimers, rateConfirmation: {...disclaimers.rateConfirmation, current: {...disclaimers.rateConfirmation.current, text: e.target.value || ''}}})
                }
                placeholder="Enter Rate Confirmation Disclaimer"
              />
            </Box>
          )}

<Box className="document-settings__section">
            <Typography variant="h6" className="document-settings__section-title">
              Trip Manifest Disclaimer
            </Typography>
            <TwField
              inputType={TwInputTypes.textarea}
              name="tripManifestDisclaimer"
              value={disclaimers.tripManifest.current.text}
              onInputChange={(e): void => setDisclaimers({...disclaimers, tripManifest: {...disclaimers.tripManifest, current: {...disclaimers.tripManifest.current, text: e.target.value || ''}}})
              }
              placeholder="Enter Trip Manifest Disclaimer"
            />
          </Box>

          <Box className="document-settings__actions">
            <TwButton
              label="Discard"
              type={TwButtonType.secondary}
              className="document-settings__button"
              action={handleDiscard}
              disabled={!hasChanges}
            />
            <TwButton
              label={isLoading ? 'Saving...' : 'Save changes'}
              type={TwButtonType.primary}
              className="document-settings__button document-settings__button--primary"
              action={handleSave}
              disabled={isLoading || !hasChanges}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentSettings;
